import { useRef } from 'react';
import { useAppDispatch, useAppSelector } from '@hooks/redux';
import { useUserEvent } from '@hooks/useUserEvent';
import { SelectedFilters } from '@constants/data-table';
import { EventControlComponent, UserEventType } from '@constants/event';
import { IApiKeysParams } from '../api/apiKeys';
import { ApiKeySort, ApiKeyStatus } from '../constants/apiKeys';
import {
  createApiKey as createApiKeyAction,
  deleteApiKey as deleteApiKeyAction,
  fetchApiKeys as fetchApiKeysAction,
  selectApiKey,
  selectApiKeys,
  selectApiToken,
  selectCreateApiKeyStatus,
  selectDeleteApiKeyStatus,
  selectFetchApiKeysStatus,
  selectUpdateApiKeyStatus,
  setApiKey as setApiKeyAction,
  updateApiKey as updateApiKeyAction,
} from '../redux/apiKeysSlice';
import {
  ApiKeyEventPayload,
  IApiKey,
  ICreateApiKeyPayload,
  IUpdateApiKeyPayload,
} from '../types/apiKeys';
import { getApiKeyFilterParams } from '../utils/apiKeys';

const useApiKeys = () => {
  const dispatch = useAppDispatch();
  const { trackUserEvent } = useUserEvent();

  const apiKey = useAppSelector(selectApiKey);
  const apiKeys = useAppSelector(selectApiKeys);
  const apiToken = useAppSelector(selectApiToken);
  const createApiKeyStatus = useAppSelector(selectCreateApiKeyStatus);
  const deleteApiKeyStatus = useAppSelector(selectDeleteApiKeyStatus);
  const fetchApiKeysStatus = useAppSelector(selectFetchApiKeysStatus);
  const updateApiKeyStatus = useAppSelector(selectUpdateApiKeyStatus);

  const filterParams = useRef<{ search: string; status: ApiKeyStatus } | null>(null);

  const createApiKey = (payload: ICreateApiKeyPayload) => dispatch(createApiKeyAction(payload));

  const fetchApiKeys = (params: IApiKeysParams) => dispatch(fetchApiKeysAction(params));

  const deleteApiKey = (apiKeyId: string) => dispatch(deleteApiKeyAction(apiKeyId));

  const setApiKey = (payload: IApiKey) => dispatch(setApiKeyAction(payload));

  const updateApiKey = (apiKeyId: string, payload: IUpdateApiKeyPayload) =>
    dispatch(updateApiKeyAction({ apiKeyId, ...payload }));

  const trackApiKeyUserEvent = async (payload: ApiKeyEventPayload) => {
    const { event, properties = {} } = payload;
    await trackUserEvent({
      type: UserEventType.CLICK as string,
      control: `${EventControlComponent.API_KEYS}/${event}`,
      properties: properties as Record<string, unknown>,
    });
  };

  const getApikeys = (
    _: number,
    pageSize: number,
    searchValue: string,
    sortValue?: string,
    filterValues?: SelectedFilters,
    after?: Record<string, unknown>,
  ) => {
    const { api_token_id: apiKeyId } = after || {};
    const status = (
      filterValues?.status?.length ? filterValues.status[0].value : ApiKeyStatus.All
    ) as ApiKeyStatus;

    const params: IApiKeysParams = getApiKeyFilterParams({
      status,
      searchValue,
      sortValue: sortValue as ApiKeySort,
    });

    fetchApiKeys({
      limit: pageSize,
      ...(apiKeyId ? { after: apiKeyId as string } : {}),
      ...params,
    });

    filterParams.current = { search: searchValue, status };
  };

  const hasActiveFilters = () => {
    if (!filterParams.current) return false;

    const { search, status } = filterParams.current;
    return !!(search.length || status !== ApiKeyStatus.All);
  };

  return {
    apiKey,
    apiKeys,
    apiToken,
    createApiKey,
    deleteApiKey,
    createApiKeyStatus,
    deleteApiKeyStatus,
    fetchApiKeys,
    fetchApiKeysStatus,
    getApikeys,
    hasActiveFilters,
    setApiKey,
    trackApiKeyUserEvent,
    updateApiKey,
    updateApiKeyStatus,
  };
};

export default useApiKeys;
